import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
  SWITCH_NUMBER_STRING,
  SWITCH_NUMBER_OP,
} from "lookup/GlobalConstants";
import {
  SWIM_STATES_FOR_SELECT,
  DEFAULT_SWIM_SORT,
  SWIM_FILTER,
  DEFAULT_FILTER,
} from "lookup/SwimConstants";
import { showNotification } from "services/NotificationService";
import ServerSideReactTable from "commons/table/ServerSideReactTable";
import { createColumnListForSwitchList } from "./CreateColumnListForHscSwitchList";
import { hasRoleIn } from "rbac/RbacHelper";
import PERMISSIONS from "rbac/Permissions.json";

const Tip = (props) => (
  <div style={{ textAlign: "center", color: "#888", fontSize: "0.9em" }}>
    <em>{props.children}</em>
  </div>
);

const DEFAULT_PAGE_SIZE = 15;

const HscSwitchListCommon = ({
  columnList: propColumnList, // Renamed to avoid conflict with state
  isSortFilterEnabledForCust,
  onNoteUpdate,
  onConfirm,
  isError,
  resetError,
  updateData: updateDataProp, // Renamed to avoid conflict with local function
  updateInterventionList,
  data,
  pages,
  loading,
  totalElements,
  listState,
  isViewable,
  toggleSelection,
  isSelectAll,
  selected,
  resetTableSelection,
}) => {
  const navigate = useNavigate();

  const [internalListState, setInternalListState] = useState({});

  const columnList = useMemo(() => createColumnListForSwitchList(
    propColumnList,
    isSortFilterEnabledForCust,
    onNoteUpdate,
    onConfirm
  ), [propColumnList, isSortFilterEnabledForCust, onNoteUpdate, onConfirm]);

  const updateData = useCallback((listOptions, preventUpdate) => {
    if (!preventUpdate) {
      listOptions.filtered = listOptions.filtered.filter((element) => {
        if (typeof element.value === "string" || Array.isArray(element.value)) {
          return element.value.length > 0;
        }
        if (typeof element.value === "boolean") {
          return true;
        }
        return element.value !== undefined && element.value !== null;
      });
      // Ensure default sorting is respected
      if (listOptions.sorted !== DEFAULT_SWIM_SORT) {
        listOptions.sorted.forEach((element) => {
          element.isDefault = false;
        });
      }

      const index = listOptions.filtered.findIndex(
        (element) => element.id === "switchNumber"
      );
      if (index !== -1) {
        listOptions.filtered[index].operator = SWITCH_NUMBER_OP;
        listOptions.filtered[index].id = SWITCH_NUMBER_STRING;
      }

      const filtered = listOptions.filtered.filter(
        (element) =>
          !(element.id === "state" && element.value === DEFAULT_FILTER)
      );

      if (filtered.length !== listOptions.filtered.length) {
        filtered.push(SWIM_FILTER.defualtFrontEnd);
        listOptions.filtered = filtered;
      }
      const index_state = listOptions.filtered.findIndex(
        (element) => element.id === "state"
      );
      if (index_state !== -1) {
        listOptions.filtered[index_state].value = listOptions.filtered[
          index_state
        ].value.map((value) =>
          typeof value === "string" ? value : value.name
        );
      }

      if (
        listOptions.filtered.filter(
          (element) =>
            element.id === "state" && element.value.includes("DEFAULT_FILTERS")
        ).length > 0
      ) {
        listOptions.filtered[index_state] = SWIM_FILTER.defualtFrontEnd;
      }

      if (updateDataProp) {
        updateDataProp(listOptions);
      } else {
        updateInterventionList(
          listOptions.pageSize,
          listOptions.page,
          listOptions.sorted,
          listOptions.filtered,
          listOptions.resized,
          listOptions.expanded
        );
      }
    } else {
      setInternalListState(listOptions);
    }
  }, [updateDataProp, updateInterventionList]);

  useEffect(() => {
    if (isError) { // Check if isError is true
      showNotification({
        title: "Error Loading Data!",
        message: "You have entered invalid information.",
        position: "bl",
        type: "error",
      });
      resetError();
    }
  }, [isError, resetError]);

  const onUpdateTable = useCallback((filterValues, sortBy, pageSize, pageNo) => {
    const newListState = {
      page: pageNo,
      pageSize: pageSize,
      sorted: sortBy || [],
      filtered: filterValues,
      resized: [],
      expanded: {},
    };
    updateData(newListState);
  }, [updateData]);

  const currentListState = listState || internalListState; // Use prop listState if available, otherwise internal

  const index = currentListState.filtered?.findIndex(
    (element) => element.id === SWITCH_NUMBER_STRING
  );
  if (index !== -1 && currentListState.filtered) {
    currentListState.filtered[index].id = "switchNumber";
  }

  const isSwimUser = hasRoleIn([PERMISSIONS.PERM_SWIM_WORKFLOW_ACCESS]);

  return (
    <Row>
      <Col md={12}>
        <ServerSideReactTable
          columnDefination={columnList}
          data={data}
          currentPage={currentListState.page}
          sorted={currentListState.sorted}
          filters={currentListState.filtered}
          totalPages={pages}
          totalRecords={totalElements == "loading" ? 0 : totalElements}
          loading={loading}
          overrideRowId={"wfItemId"}
          pageSizeOptions={[5, 10, 15, 20, 30, 50]}
          defaultPageSize={DEFAULT_PAGE_SIZE}
          onUpdateTableData={onUpdateTable}
          onRowClick={(row) => {
            if (row && isSwimUser) {
              // this.props.updateIndexOnList(row.index); // This prop is not passed to functional component
              navigate(`/switch/${row.original.wfItemId}`);
            }
          }}
          enableMultiRowSelection={!!toggleSelection}
          addRowSelection={
            isViewable !== undefined && isViewable === true
              ? false
              : !!toggleSelection
          }
          onRowSelect={toggleSelection}
          toggleAllRowsSelect={isSelectAll}
          globalSelected={selected ? selected : []}
          resetTableSelection={resetTableSelection ? resetTableSelection : null}
        />
        <Tip>
          Tip: Hold shift when sorting to multi-sort and disable sort!
        </Tip>
      </Col>
    </Row>
  );
};

export default HscSwitchListCommon;
